import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ListingCard } from "@/components/ListingCard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Plus, Bell, Trash2, Zap, Package, Eye, Search as SearchIcon, BellRing, UploadCloud, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [saved, setSaved] = useState([]);
  const [alerts, setAlerts] = useState([]);

  const loadSaved = () => api.get("/saved-searches").then((r) => setSaved(r.data));
  useEffect(() => {
    api.get("/listings/mine").then((r) => setListings(r.data));
    api.get("/alerts").then((r) => setAlerts(r.data));
    loadSaved();
  }, []);

  const delSearch = async (sid) => { await api.delete(`/saved-searches/${sid}`); toast.success("Removed"); loadSaved(); };
  const sendDigest = async () => {
    try {
      const r = await api.post("/digest/send");
      if (r.data.sent) toast.success(`Digest emailed — ${r.data.count} new listing${r.data.count === 1 ? "" : "s"}`);
      else toast.info(r.data.message || "No new matches this week");
    } catch { toast.error("Could not send digest"); }
  };
  const toggleAlerts = async (sid, enabled) => {
    setSaved((prev) => prev.map((s) => s.id === sid ? { ...s, alerts_enabled: enabled } : s));
    try { await api.put(`/saved-searches/${sid}/alerts`, { alerts_enabled: enabled }); toast.success(enabled ? "Alerts on" : "Alerts off"); }
    catch { toast.error("Could not update"); loadSaved(); }
  };
  const runSaved = (filters) => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    navigate(`/search?${p.toString()}`);
  };

  const stats = {
    active: listings.filter((l) => l.status === "active").length,
    boosted: listings.filter((l) => l.boosted).length,
    views: listings.reduce((a, l) => a + (l.views || 0), 0),
  };

  return (
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-900 text-3xl uppercase tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Welcome back, {user?.name?.split(" ")[0]}</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.is_dealer && (
            <Button data-testid="dash-import-btn" onClick={() => navigate("/dealer/import")} variant="outline" className="border-border bg-transparent rounded-sm font-bold gap-1.5"><UploadCloud className="w-4 h-4" /> Import CSV</Button>
          )}
          <Button data-testid="dash-new-listing-btn" onClick={() => navigate("/sell")} className="bg-blaze hover:bg-blaze-hover text-white rounded-sm font-bold gap-1.5"><Plus className="w-4 h-4" /> New Listing</Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-8">
        {[
          { icon: Package, label: "Active Listings", value: stats.active },
          { icon: Zap, label: "Boosted", value: stats.boosted },
          { icon: Eye, label: "Total Views", value: stats.views },
        ].map((s, i) => (
          <div key={i} className="bg-[#141414] border border-border rounded-sm p-4">
            <s.icon className="w-5 h-5 text-blaze" />
            <div className="font-heading font-900 text-3xl mt-2 spec-block">{s.value}</div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{s.label}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="listings">
        <TabsList className="bg-[#141414] border border-border rounded-sm p-1">
          <TabsTrigger value="listings" data-testid="tab-listings" className="data-[state=active]:bg-blaze data-[state=active]:text-white rounded-sm font-bold uppercase text-xs tracking-wider">My Listings</TabsTrigger>
          <TabsTrigger value="saved" data-testid="tab-saved" className="data-[state=active]:bg-blaze data-[state=active]:text-white rounded-sm font-bold uppercase text-xs tracking-wider">Saved Searches</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts" className="data-[state=active]:bg-blaze data-[state=active]:text-white rounded-sm font-bold uppercase text-xs tracking-wider">Alerts{alerts.length > 0 ? ` (${alerts.length})` : ""}</TabsTrigger>
        </TabsList>

        <TabsContent value="listings" className="mt-5">
          {listings.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-sm">
              <Package className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No listings yet.</p>
              <Button onClick={() => navigate("/sell")} className="mt-4 bg-blaze hover:bg-blaze-hover rounded-sm font-bold">Create your first listing</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {listings.map((l, i) => (
                <div key={l.id} className="relative">
                  <ListingCard listing={l} index={i} />
                  {l.status !== "active" && <div className="absolute top-2 right-2 bg-destructive text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm z-10">{l.status}</div>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="saved" className="mt-5">
          {saved.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-sm">
              <Bell className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No saved searches. Save a search to track rare specs.</p>
              <Button onClick={() => navigate("/search")} className="mt-4 bg-blaze hover:bg-blaze-hover rounded-sm font-bold gap-1.5"><SearchIcon className="w-4 h-4" /> Browse marketplace</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {saved.map((s) => (
                <div key={s.id} data-testid={`saved-search-${s.id}`} className="bg-[#141414] border border-border rounded-sm p-4 flex items-center justify-between gap-3 hover:border-blaze/50 transition-colors">
                  <button onClick={() => runSaved(s.filters)} className="text-left flex-1 min-w-0">
                    <div className="font-heading font-800 text-sm spec-block truncate">{s.name}</div>
                    <div className="text-xs text-blaze mt-1 flex items-center gap-1"><Bell className="w-3 h-3" /> {s.match_count} matching listing{s.match_count === 1 ? "" : "s"}</div>
                  </button>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex items-center gap-1.5" title="Email alerts">
                      <BellRing className={`w-3.5 h-3.5 ${s.alerts_enabled ? "text-blaze" : "text-muted-foreground"}`} />
                      <Switch data-testid={`alerts-toggle-${s.id}`} aria-label="Toggle email alerts for this saved search" checked={s.alerts_enabled !== false} onCheckedChange={(v) => toggleAlerts(s.id, v)} className="data-[state=checked]:bg-blaze" />
                    </div>
                    <Button data-testid={`del-saved-${s.id}`} onClick={() => delSearch(s.id)} variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="alerts" className="mt-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <p className="text-xs text-muted-foreground">Your weekly "just listed" digest emails new matches for your saved specs.</p>
            <Button data-testid="send-digest-btn" onClick={sendDigest} variant="outline" className="border-border bg-transparent rounded-sm gap-2 text-sm"><Mail className="w-4 h-4 text-blaze" /> Email me my digest now</Button>
          </div>
          {alerts.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-sm">
              <BellRing className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No alerts yet. Turn on alerts for a saved search and we'll ping you when a match drops.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {alerts.map((a) => (
                <button key={a.id} data-testid={`alert-${a.id}`} onClick={() => navigate(`/listing/${a.listing_id}`)} className="w-full text-left bg-[#141414] border border-border rounded-sm p-4 flex items-center gap-3 hover:border-blaze/50 transition-colors">
                  <div className="w-9 h-9 bg-blaze/15 rounded-sm flex items-center justify-center shrink-0"><BellRing className="w-4 h-4 text-blaze" /></div>
                  <div className="min-w-0">
                    <div className="font-heading font-800 text-sm truncate">{a.listing_title}</div>
                    <div className="text-xs text-muted-foreground">Matched "{a.search_name}" · {new Date(a.created_at).toLocaleDateString()}</div>
                  </div>
                  <span className="ml-auto text-xs text-blaze shrink-0">View →</span>
                </button>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
